/**
 * Every shape a number could be.
 *
 * Solving this puzzle is choosing, for each number, which block of cells it
 * names. So the search works on those blocks directly: for a 5, every five-cell
 * connected shape that contains the 5 and could legally be its region. A block
 * is a `Mask`, its border is another, and testing whether two blocks can coexist
 * is then a pair of AND instructions rather than a walk over the grid.
 *
 * Two rules can be applied here, once, instead of inside the search:
 *
 *   - A region holds exactly one number, so a block may not contain any other
 *     number's cell.
 *   - Same-shade regions may not share an edge, so a block may not contain a
 *     cell that touches another number of its own shade. (Those two statements
 *     are the same one: a cell of block A touching number B is number B sitting
 *     on A's border.)
 *
 * What is left is decided during the search, where it depends on the other
 * blocks chosen: no overlaps, and no two same-shade blocks touching.
 */

import {
  type Geometry,
  type Mask,
  borderOf,
  maskCells,
  maskHas,
  maskKey,
  maskOf,
  maskWith,
} from './board';
import { MAX_REGION, type Clue } from './types';

export interface Placement {
  /** Index into the puzzle's clue list. */
  clue: number;
  cells: Mask;
  /** The same cells as a list, for the callers that have to walk them. */
  list: number[];
  /** The cells around the block, which its shade's opposite must fill. */
  border: Mask;
}

/**
 * Guard against an enumeration nobody wants to wait for.
 *
 * `MAX_REGION` keeps the real counts in the low thousands; this only fires if a
 * board is built with a number bigger than the generator is allowed to make, and
 * it fails loudly rather than hanging the worker.
 */
const PLACEMENT_LIMIT = 40000;

export function placementsFor(geo: Geometry, clues: readonly Clue[], index: number): Placement[] {
  const clue = clues[index] as Clue;

  // Cells this number's region can never contain: another number, or anything
  // touching another number of the same shade.
  const blocked = new Uint8Array(geo.cells);
  clues.forEach((other, position) => {
    if (position === index) return;
    blocked[other.cell] = 1;
    if (other.shade !== clue.shade) return;
    for (const neighbor of geo.neighbors[other.cell] as number[]) blocked[neighbor] = 1;
  });
  if (blocked[clue.cell] === 1) return [];

  // Grown a cell at a time, keyed by mask so the same shape reached by two
  // different orders is kept once.
  let level = new Map<string, Mask>();
  const seed = maskOf([clue.cell]);
  level.set(maskKey(seed), seed);

  // A `?` is every size at once, so for one of those each level on the way up is
  // an answer rather than a step towards one. It costs about a third more than
  // the largest size alone — the levels below the top are small next to it — and
  // it is the whole reason `MAX_REGION` has to be a number the player knows.
  const hidden = clue.value === null;
  const grownTo = clue.value ?? MAX_REGION;
  const shapes: Mask[] = hidden ? [seed] : [];

  for (let size = 1; size < grownTo; size += 1) {
    const next = new Map<string, Mask>();
    for (const mask of level.values()) {
      for (const cell of maskCells(mask)) {
        for (const neighbor of geo.neighbors[cell] as number[]) {
          if (blocked[neighbor] === 1 || maskHas(mask, neighbor)) continue;
          const grown = maskWith(mask, neighbor);
          next.set(maskKey(grown), grown);
        }
      }
    }
    if (shapes.length + next.size > PLACEMENT_LIMIT) {
      throw new Error(`number ${clue.value ?? '?'} has more shapes than the solver will enumerate`);
    }
    level = next;
    if (hidden) shapes.push(...level.values());
  }
  if (!hidden) shapes.push(...level.values());

  return shapes.map((cells) => ({
    clue: index,
    cells,
    list: maskCells(cells),
    border: borderOf(geo, cells),
  }));
}

export function allPlacements(geo: Geometry, clues: readonly Clue[]): Placement[][] {
  return clues.map((_, index) => placementsFor(geo, clues, index));
}
